/**
 * ISO country/region helpers — countries list, regions by country,
 * and Google Places state → ISO region mapping.
 */

import {
  getCountries,
  getStatesForCountry,
  ensureGeoDataLoaded,
  isLanguageLoaded,
  AVAILABLE_LANGUAGES,
  type SupportedLanguage,
  type Country,
  type State,
} from './iso3166.js';

export type { SupportedLanguage, Country, State };
export { AVAILABLE_LANGUAGES };

export interface ISOCountry {
  iso: string;
  iso3: string;
  numeric: number;
  name: string;
}

export interface ISORegion {
  iso: string;
  name: string;
}

export class ISODataCore {
  /** Fetch countries + regions from CDN for a language. Call before sync getters. */
  static ensureLoaded(language: SupportedLanguage = 'en'): Promise<void> {
    return ensureGeoDataLoaded(language);
  }

  /** Get all countries as a Record keyed by ISO code. */
  static getCountries(language: SupportedLanguage = 'en'): Record<string, ISOCountry> {
    const arr = getCountries(language);
    const result: Record<string, ISOCountry> = {};
    for (const c of arr) {
      result[c.code] = { iso: c.code, iso3: c.iso3 ?? '', numeric: c.numeric ?? 0, name: c.name };
    }
    return result;
  }

  /** Get all countries as a sorted array of { value, label } for select inputs. */
  static getCountryOptions(language: SupportedLanguage = 'en'): Array<{ value: string; label: string }> {
    return getCountries(language).map((c) => ({ value: c.code, label: c.name }));
  }

  /** Get regions/states for a country. */
  static getRegions(countryCode: string, language: SupportedLanguage = 'en'): ISORegion[] {
    return getStatesForCountry(countryCode, language).map((s) => ({ iso: s.code, name: s.name }));
  }

  /** Get regions as a sorted array of { value, label } for select inputs. */
  static getRegionOptions(countryCode: string, language: SupportedLanguage = 'en'): Array<{ value: string; label: string }> {
    return ISODataCore.getRegions(countryCode, language).map((r) => ({ value: r.iso, label: r.name }));
  }

  /** Find a specific region by ISO code. */
  static findRegion(countryCode: string, regionCode: string, language: SupportedLanguage = 'en'): ISORegion | null {
    return ISODataCore.getRegions(countryCode, language).find((r) => r.iso === regionCode) ?? null;
  }

  /**
   * Map a Google Places administrative_area to an ISO region.
   * Tries exact ISO match, then name match, then partial match.
   */
  static mapGoogleToISO(
    googleState: string,
    googleStateLong: string,
    countryCode: string,
    language: SupportedLanguage = 'en',
  ): ISORegion | null {
    const regions = ISODataCore.getRegions(countryCode, language);
    if (regions.length === 0) return null;

    let match = regions.find((r) => r.iso === googleState);
    if (match) return match;

    match = regions.find((r) => r.name.toLowerCase() === googleState.toLowerCase());
    if (match) return match;

    match = regions.find((r) => r.name.toLowerCase() === googleStateLong.toLowerCase());
    if (match) return match;

    match = regions.find(
      (r) =>
        r.name.toLowerCase().includes(googleStateLong.toLowerCase()) ||
        googleStateLong.toLowerCase().includes(r.name.toLowerCase()),
    );

    return match ?? null;
  }

  static isLanguageLoaded(language: SupportedLanguage): boolean {
    return isLanguageLoaded(language);
  }

  static getAvailableLanguages(): readonly SupportedLanguage[] {
    return AVAILABLE_LANGUAGES;
  }
}
